/* ============================================================================
 * battery-live.js — what a run of Battery Status readings does and does not
 * say. `window.BatteryLive`; no DOM, UMD, so the same file runs in the page and
 * in node.
 *
 * WHY THIS EXISTS. The API hands a page four fields, and a page that prints
 * them as they come tells three lies on an ordinary laptop:
 *
 *   "Charging".  The field is called `charging`, but on Windows Chromium sets
 *       it from the AC line (`ACLineStatus != OFFLINE`) and on Android from
 *       `plugged != 0`. It means PLUGGED IN. A laptop held at 80 % by its own
 *       charge limit reports `charging: true` for as long as it sits there.
 *       So the page says "plugged in" until it has SEEN the level rise, and
 *       says the level is not rising once it has watched long enough to know.
 *
 *   "Until full: ∞".  On Windows `chargingTime` is +Infinity whenever the
 *       level is below 100 — the OS call Chromium uses has no such figure —
 *       and Android reports it only where the kernel exposes an average
 *       current. Infinity is the spec's word for "not known", and it reads as
 *       "never". An unknown time is shown as unknown.
 *
 *   "+119.9 %/h".  The level arrives in whole steps. Dividing the change by
 *       the time since the page opened measures where in a step the page
 *       happened to open: one step half a minute in is "120 % an hour". The
 *       rate is taken from the TIMES of the steps (ChargerTestCore.rate), and
 *       there is none until two steps have been seen.
 *
 * Everything here is a pure function of the samples so that each of those can
 * be held by a test.
 *
 * A sample is { t: ms, pct: 0–100, plugged: boolean }.
 * ==========================================================================*/
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.BatteryLive = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var C = {
        /* How long the level may stand still on the charger before the page
           says so. The slowest charging that is still charging — a 5 W port
           into a 60 Wh pack — is a step every seven or eight minutes, so two
           of those with nothing is a level that is not rising. */
        HELD_AFTER_MS: 15 * 60 * 1000,
        /* One measured interval between two steps is the least a rate can be
           read from. */
        RATE_MIN_STEPS: 2,
        /* …and the most that are used. The rate describes what the machine is
           doing now, and "now" is counted in steps, not minutes: six steps are
           eight minutes of heavy use or half an hour of light use, and either
           way the same amount of evidence. */
        RATE_MAX_STEPS: 6,
        /* Percent per hour. The top tenth of laptop use in the battery-report
           corpus starts at 0.63 of a battery per hour. */
        FAST_DRAIN: 60,
    };

    /** The readings since the plug last went in or came out. */
    function run(samples) {
        if (!samples.length) return [];
        var last = samples[samples.length - 1], i = samples.length - 1;
        while (i > 0 && samples[i - 1].plugged === last.plugged) i--;
        return samples.slice(i);
    }

    /**
     * What the plug is doing, as far as it has been observed.
     *   'discharging'  on battery
     *   'full'         plugged in at 100
     *   'charging'     plugged in and the level was seen to rise
     *   'held'         plugged in, watched for HELD_AFTER_MS, no rise
     *   'plugged'      plugged in, not watched long enough to say which
     */
    function plug(samples, now) {
        var r = run(samples);
        if (!r.length) return null;
        var last = r[r.length - 1];
        if (!last.plugged) return 'discharging';
        if (last.pct >= 100) return 'full';
        var since = r[0].t, rose = false, i;
        for (i = 1; i < r.length; i++) {
            if (r[i].pct > r[i - 1].pct) { since = r[i].t; rose = true; }
        }
        if (now - since >= C.HELD_AFTER_MS) return 'held';
        return rose ? 'charging' : 'plugged';
    }

    /**
     * Percent per hour, signed, from the times of the level's steps; null
     * until there are enough of them.
     * @param core  ChargerTestCore
     */
    function rate(samples, core) {
        var r = run(samples).map(function (s) { return { t: s.t / 1000, level: s.pct, plugged: s.plugged }; });
        if (!core || r.length < 2) return null;
        var steps = core.transitions(r);
        if (steps.length > C.RATE_MAX_STEPS) {
            /* From the reading just before the oldest step that is kept. */
            var first = steps[steps.length - C.RATE_MAX_STEPS].t, i = 0;
            while (i < r.length && r[i].t < first) i++;
            r = r.slice(Math.max(0, i - 1));
        }
        var f = core.rate(r, { minTransitions: C.RATE_MIN_STEPS });
        if (!f.ok) return null;
        /* On the charger a falling fit, or on battery a rising one, is a gauge
           settling after the plug moved, not a rate. */
        var last = r[r.length - 1];
        if (last.plugged ? f.pctPerHour <= 0 : f.pctPerHour >= 0) return null;
        return f.pctPerHour;
    }

    /** Seconds until empty at a measured discharge rate; null without one. */
    function secondsToEmpty(pct, pctPerHour) {
        if (!(pctPerHour < 0) || !(pct > 0)) return null;
        return pct / -pctPerHour * 3600;
    }

    /**
     * A time the system reported, or null when it reported "not known" — which
     * the API spells Infinity, and for a time to full on a pack that is not
     * full also 0.
     */
    function reported(seconds) {
        return (typeof seconds === 'number' && isFinite(seconds) && seconds > 0) ? seconds : null;
    }

    return {
        C: C, run: run, plug: plug, rate: rate,
        secondsToEmpty: secondsToEmpty, reported: reported,
    };
}));
